import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Eye, Gem, Layers, Loader2, LockKeyhole, Plus, Search, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { db } from "@/lib/billing";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { templateAllowed, usePackageAccess, type TemplateRecord } from "@/lib/packageAccess";
import { ALL_CATEGORIES, CATEGORY_LABELS_FR, CATEGORY_LABELS_EN, type CategoryKey } from "@/templates";
import { normalizeCategory } from "@/templates/normalizeCategory";

type WebsiteRow = { id: string; name: string };

async function fetchTemplates(): Promise<TemplateRecord[]> {
  const { data, error } = await db.from("site_templates").select("*").order("sort_order");
  if (error) throw error;
  return data ?? [];
}

async function fetchOwnedWebsites(userId: string | undefined): Promise<WebsiteRow[]> {
  if (!userId) return [];
  const { data, error } = await db.from("websites").select("id, name").eq("owner_id", userId).order("created_at");
  if (error) return [];
  return (data ?? []) as WebsiteRow[];
}

async function fetchSuggestedCategoryRaw(userId: string | undefined): Promise<string | null> {
  if (!userId) return null;
  const { data } = await db
    .from("project_intakes")
    .select("category")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return (data?.category as string | undefined) ?? null;
}

async function claimExclusive(templateId: string, websiteId: string) {
  const { data, error } = await db.rpc("claim_exclusive_template", {
    _template_id: templateId,
    _website_id: websiteId,
  });
  if (error) throw error;
  return data;
}

type CategoryFilter = "all" | CategoryKey;
type ExclusivityFilter = "all" | "public" | "exclusive";

export default function TemplatesPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [exclusivity, setExclusivity] = useState<ExclusivityFilter>("all");
  const [reserveTarget, setReserveTarget] = useState<TemplateRecord | null>(null);
  const [reserveWebsiteId, setReserveWebsiteId] = useState<string>("");

  const access = usePackageAccess();
  const { user } = useAuth();

  const { data: templates = [], isLoading, error } = useQuery({ queryKey: ["site-templates"], queryFn: fetchTemplates });
  const { data: ownedWebsites = [] } = useQuery({
    queryKey: ["owned-websites", user?.id],
    queryFn: () => fetchOwnedWebsites(user?.id),
    enabled: !!user,
  });
  const { data: suggestedCategoryRaw } = useQuery({
    queryKey: ["templates-suggested-category-raw", user?.id],
    queryFn: () => fetchSuggestedCategoryRaw(user?.id),
    enabled: !!user,
  });
  const suggestedCategory = useMemo(() => normalizeCategory(suggestedCategoryRaw), [suggestedCategoryRaw]);
  const ownedWebsiteIds = useMemo(() => new Set(ownedWebsites.map((w) => w.id)), [ownedWebsites]);

  const labels = (i18n.language?.startsWith("en") ? CATEGORY_LABELS_EN : CATEGORY_LABELS_FR) as Record<CategoryKey, string>;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return templates
      .filter((tpl) => category === "all" || tpl.category === category)
      .filter((tpl) => exclusivity === "all" || (exclusivity === "exclusive" ? tpl.is_exclusive : !tpl.is_exclusive))
      .filter((tpl) =>
        [tpl.name, tpl.description ?? "", tpl.category ?? "", tpl.target_niche ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
  }, [search, category, exclusivity, templates]);

  const counts = useMemo(() => ({
    public: templates.filter((tpl) => !tpl.is_exclusive).length,
    exclusive: templates.filter((tpl) => tpl.is_exclusive).length,
  }), [templates]);

  const suggested = useMemo(() => {
    if (!suggestedCategory) return [];
    return templates.filter((tpl) => tpl.category === suggestedCategory).slice(0, 3);
  }, [suggestedCategory, templates]);

  const reserveMutation = useMutation({
    mutationFn: ({ templateId, websiteId }: { templateId: string; websiteId: string }) => claimExclusive(templateId, websiteId),
    onSuccess: (_data, vars) => {
      toast({ description: t("marketing.templates.exclusive.claimSuccess", { name: reserveTarget?.name ?? "" }) });
      queryClient.invalidateQueries({ queryKey: ["site-templates"] });
      setReserveTarget(null);
      setReserveWebsiteId("");
      void vars;
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string })?.code;
      const message =
        code === "23505" ? t("marketing.templates.exclusive.claimErrorAlreadyTaken")
        : code === "42501" ? t("marketing.templates.exclusive.claimErrorTier")
        : ((err as Error)?.message ?? "Error");
      toast({ variant: "destructive", description: message });
    },
  });

  function openReserve(tpl: TemplateRecord) {
    setReserveTarget(tpl);
    setReserveWebsiteId(ownedWebsites[0]?.id ?? "");
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold">{t("nav.templates")}</h1>
          <p className="text-xs text-muted-foreground">
            {t("marketing.templates.headerSubtitle")}
            {templates.length > 0 && <span className="ml-2 opacity-70">({counts.public} + {counts.exclusive})</span>}
          </p>
        </div>
        {access.isAdmin && <Button asChild size="sm"><Link to="/templates/create"><Plus className="mr-2 h-4 w-4" />{t("common.create")}</Link></Button>}
      </header>

      <main className="space-y-6 p-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder={t("common.search") + "..."} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-1 rounded-md border border-border p-1">
            <Button type="button" variant={exclusivity === "all" ? "default" : "ghost"} size="sm" onClick={() => setExclusivity("all")}>
              {t("marketing.templates.filterAll")}
            </Button>
            <Button type="button" variant={exclusivity === "public" ? "default" : "ghost"} size="sm" onClick={() => setExclusivity("public")}>
              {t("marketing.templates.filterPublic")} <span className="ml-1 opacity-60">{counts.public}</span>
            </Button>
            <Button type="button" variant={exclusivity === "exclusive" ? "default" : "ghost"} size="sm" onClick={() => setExclusivity("exclusive")}>
              <Gem className="mr-1.5 h-3.5 w-3.5" />
              {t("marketing.templates.filterExclusive")} <span className="ml-1 opacity-60">{counts.exclusive}</span>
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant={category === "all" ? "default" : "outline"} onClick={() => setCategory("all")}>
            {t("marketing.templates.categoryAll")}
          </Button>
          {ALL_CATEGORIES.map((c) => (
            <Button
              key={c}
              type="button"
              size="sm"
              variant={category === c ? "default" : "outline"}
              onClick={() => setCategory(c)}
              title={suggestedCategory === c ? t("marketing.templates.suggestedHeading", { category: labels[c] }) : undefined}
            >
              {labels[c]}
              {suggestedCategory === c && <Sparkles className="ml-1.5 h-3 w-3" />}
            </Button>
          ))}
        </div>

        {category === "all" && exclusivity === "all" && suggested.length > 0 && suggestedCategory && (
          <section className="rounded-md border border-dashed border-border bg-muted/30 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium">
              <Sparkles className="h-4 w-4 text-accent-press" />
              {t("marketing.templates.suggestedHeading", { category: labels[suggestedCategory] })}
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {suggested.map((tpl) => (
                <Link key={tpl.id} to={`/templates/${tpl.id}/preview`} className="group flex gap-3 rounded border border-border bg-card p-2 transition hover:border-accent-press">
                  {tpl.preview_image_url ? (
                    <img src={tpl.preview_image_url} alt="" className="h-16 w-24 flex-shrink-0 rounded-sm object-cover" />
                  ) : (
                    <div className="h-16 w-24 flex-shrink-0 rounded-sm bg-muted" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium group-hover:text-accent-press">{tpl.name}</p>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{tpl.description}</p>
                    {tpl.is_exclusive && <Badge variant="outline" className="mt-1 border-accent-press text-[10px] text-accent-press"><Gem className="mr-1 h-2.5 w-2.5" />{t("marketing.templates.exclusive.badgeUnique")}</Badge>}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {isLoading || access.isLoading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <Card><CardContent className="p-6 text-sm text-destructive">{t("common.error")}</CardContent></Card>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="flex flex-col items-center justify-center py-16 text-center"><Layers className="mb-3 h-10 w-10 text-muted-foreground" /><p className="font-medium">{t("common.noResults")}</p></CardContent></Card>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((template) => {
              const allowed = access.isAdmin || templateAllowed(access.package, template);
              const claimedByOther =
                template.is_exclusive &&
                template.claimed_by_website_id !== null &&
                !ownedWebsiteIds.has(template.claimed_by_website_id);
              const claimedByMe =
                template.is_exclusive &&
                template.claimed_by_website_id !== null &&
                ownedWebsiteIds.has(template.claimed_by_website_id);
              const reservable = template.is_exclusive && template.claimed_by_website_id === null;
              const editionLabel =
                template.exclusivity_edition === "signature" ? t("marketing.templates.exclusive.badgeSignature")
                : template.exclusivity_edition === "numbered" ? t("marketing.templates.exclusive.badgeNumbered")
                : t("marketing.templates.exclusive.badgeUnique");

              return (
                <Card key={template.id} className={!allowed || claimedByOther ? "opacity-75" : ""}>
                  {template.preview_image_url ? (
                    <img src={template.preview_image_url} alt={template.name} className="h-44 w-full rounded-t-md object-cover object-top" />
                  ) : (
                    <div className="flex h-44 items-center justify-center rounded-t-md bg-muted"><Layers className="h-10 w-10 text-muted-foreground" /></div>
                  )}
                  <CardHeader className="space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={template.is_active ? "default" : "secondary"}>{template.is_active ? t("common.enabled") : t("common.disabled")}</Badge>
                        {template.is_exclusive && (
                          <Badge variant="outline" className="border-accent-press text-accent-press">
                            <Gem className="mr-1 h-3 w-3" />
                            {editionLabel}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="min-h-10 text-sm leading-6 text-muted-foreground">{template.description}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline">{template.template_type}</Badge>
                      <Badge variant="outline">{template.language}</Badge>
                      {template.layout_key && <Badge variant="outline">{template.layout_key}</Badge>}
                      {template.required_package_slug && <Badge variant="secondary">{template.required_package_slug}</Badge>}
                      {claimedByOther && <Badge variant="destructive">{t("marketing.templates.exclusive.stateClaimedByOther")}</Badge>}
                      {claimedByMe && <Badge variant="default">{t("marketing.templates.exclusive.stateClaimedByMe")}</Badge>}
                      {reservable && <Badge variant="secondary">{t("marketing.templates.exclusive.stateAvailable")}</Badge>}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(template.features ?? []).slice(0, 4).map((feature) => <span key={feature} className="rounded bg-muted px-2 py-1 text-[10px] text-muted-foreground">{feature}</span>)}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {allowed && !claimedByOther ? (
                        <Button asChild size="sm" variant="outline"><Link to={`/templates/${template.id}`}>{t("common.actions")}</Link></Button>
                      ) : claimedByOther ? (
                        <Button size="sm" variant="outline" disabled><LockKeyhole className="mr-2 h-4 w-4" />{t("marketing.templates.exclusive.stateClaimedByOther")}</Button>
                      ) : (
                        <Button asChild size="sm" variant="outline"><Link to="/billing/plans"><LockKeyhole className="mr-2 h-4 w-4" />Upgrade</Link></Button>
                      )}
                      <Button asChild size="sm" variant="outline"><Link to={`/templates/${template.id}/preview`}><Eye className="mr-2 h-4 w-4" />{t("common.preview")}</Link></Button>
                      {reservable && allowed && (
                        <Button size="sm" onClick={() => openReserve(template)} disabled={ownedWebsites.length === 0}>
                          <Gem className="mr-2 h-4 w-4" />
                          {t("marketing.templates.exclusive.claimConfirmTitle")}
                        </Button>
                      )}
                      {access.isAdmin && <Button asChild size="sm" variant="outline"><Link to={`/templates/${template.id}/edit`}>{t("common.edit")}</Link></Button>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <AlertDialog open={!!reserveTarget} onOpenChange={(open) => { if (!open) { setReserveTarget(null); setReserveWebsiteId(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("marketing.templates.exclusive.claimConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left">
                <p>{t("marketing.templates.exclusive.claimConfirmBody", { name: reserveTarget?.name ?? "" })}</p>
                <p className="text-xs text-muted-foreground">{t("marketing.templates.exclusive.explainer")}</p>
                {ownedWebsites.length > 1 && (
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">{t("common.website")}</label>
                    <select
                      value={reserveWebsiteId}
                      onChange={(e) => setReserveWebsiteId(e.target.value)}
                      className="block w-full rounded border border-input bg-background px-3 py-2 text-sm"
                    >
                      {ownedWebsites.map((w) => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {ownedWebsites.length === 1 && (
                  <p className="text-xs text-muted-foreground">{t("common.website")} : <strong>{ownedWebsites[0].name}</strong></p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={!reserveTarget || !reserveWebsiteId || reserveMutation.isPending}
              onClick={() => {
                if (!reserveTarget || !reserveWebsiteId) return;
                reserveMutation.mutate({ templateId: reserveTarget.id, websiteId: reserveWebsiteId });
              }}
            >
              {reserveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gem className="mr-2 h-4 w-4" />}
              {t("marketing.templates.exclusive.claimConfirmTitle")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
